import { useEffect, useRef } from 'react';
import { CloseIcon } from '@/components/icons';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  labelledBy?: string;
  children: React.ReactNode;
}

/**
 * Accessible centered modal dialog. Closes on Escape and backdrop click,
 * locks body scroll while open, and moves focus into the dialog.
 */
export default function Modal({ open, onClose, title, subtitle, labelledBy, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-ink-900/60 p-3 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl outline-none sm:max-h-[calc(100dvh-2rem)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="סגירה"
          className="absolute top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/80 text-ink-500 shadow-sm transition hover:bg-white hover:text-ink-800 ltr:right-4 rtl:left-4"
        >
          <CloseIcon width={18} height={18} />
        </button>

        <div className="overflow-y-auto px-5 pb-6 pt-8 sm:px-8">
          {title && (
            <h2 id={labelledBy} className="text-center text-2xl font-extrabold tracking-tight text-ink-900">
              {title}
            </h2>
          )}
          {subtitle && <p className="mb-6 mt-2 text-center text-sm text-ink-500">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
